<script setup lang="ts">
import { periodPriceText, weeklyPriceRangeText } from '~/helpers/priceHelper'
import type { HouseAskAQuestion } from '#build/components'
import type { HouseDetails } from '~/types/house/types'

const {
  form,
  house,
  showSubmitBtn = false,
} = defineProps<{
  form: InstanceType<typeof HouseAskAQuestion> | null
  house: HouseDetails
  showSubmitBtn?: boolean
}>()
const emits = defineEmits<{ finish: [] }>()
const { booking: pageBooking } = useHouseInquiryBooking()
const { booking, fetchAndsetSelectedPeriod } = useHouseInquiryBooking(house.id)
if (!booking.value.startDate && pageBooking.value.startDate) {
  booking.value.startDate = pageBooking.value.startDate
}
if (!booking.value.endDate && pageBooking.value.endDate) {
  booking.value.endDate = pageBooking.value.endDate
}
const i18n = useI18n()
const { isMobile } = useBreakpoint()
const { currency } = useUser()
const { userIsAdmin, userIsPartner } = useAuth()

const loading = ref(false)
const priceText = computed(() => {
  if (booking.value.endDate) {
    const { text } = periodPriceText(
      i18n,
      {
        currency: currency.value,
        display_prices: booking.value.price.value as number,
        endAt: booking.value.endDate as string,
        startAt: booking.value.startDate as string,
        publicPrice: booking.value.price.value as number,
      },
      false,
      userIsPartner.value,
    )
    const [price, nights = ''] = text.split('-')
    return price.concat(nights ? ` (${nights.trim()})` : '')
  }
  return weeklyPriceRangeText(
    i18n,
    house.minPrice[currency.value],
    house.maxPrice[currency.value],
    currency.value,
    true,
    userIsAdmin.value,
  )
})

const goNext = () => {
  if (booking.value.currentStep <= 1) {
    loading.value = true
    form
      ?.submitForm(Number(house.id))
      .catch(() => void 0)
      .finally(() => (loading.value = false))
  } else {
    loading.value = false
    emits('finish')
  }
}

watch(
  booking,
  (val) => {
    if (!val.endDate || !val.startDate) {
      loading.value = false
      return
    }
    loading.value = true
    fetchAndsetSelectedPeriod({
      currency: currency.value,
      houseId: Number(house.id),
      displayAvailabilities: true,
      displayPrices: true,
      minPrice: house.minPrice,
      maxPrice: house.maxPrice,
    })
      .catch(() => undefined)
      .finally(() => (loading.value = false))
  },
  { immediate: import.meta.client },
)
</script>

<template>
  <!-- Show house information + picture at 1st step only -->
  <div v-if="booking.currentStep === 0" class="flex items-center gap-4">
    <BaseNuxtImg
      class="!size-12 rounded sm:!size-20"
      :src="house.firstPhoto.url"
      sizes="96px sm:160px"
    />
    <div class="flex h-full flex-col items-start justify-center">
      <h3 class="m-0 text-md sm:text-xl">{{ house.name }}</h3>
      <p :class="['m-0 text-xs sm:text-md', { 'animate-pulse': loading }]">
        {{ priceText }}
      </p>
    </div>
  </div>

  <BaseButton
    v-if="showSubmitBtn"
    color="secondary"
    :block-full="isMobile && booking.currentStep >= 1"
    :disabled="booking.currentStep <= 1 && !form?.formIsValid"
    :loader="loading"
    @click="goNext"
  >
    {{ $t(`inquiry.footer.cta[${booking.currentStep}]`, Number(isMobile) + 1) }}
  </BaseButton>
</template>
